import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Slider, 
  TextField, 
  Button, 
  Card, 
  CardContent,
  Grid,
  Box,
  Paper,
  Link,
  InputAdornment
} from '@mui/material';

const BCBAUtilizationCalculator = () => {
  const [bcbas, setBcbas] = useState(15);
  const [avgUtilization, setAvgUtilization] = useState(10);
  const [targetUtilization, setTargetUtilization] = useState(50);
  const [clientsPerBCBA, setClientsPerBCBA] = useState(10);
  const [hoursPerMonth, setHoursPerMonth] = useState(2);
  const [hourlyRate, setHourlyRate] = useState(120);
  const [netRevenuePotential, setNetRevenuePotential] = useState(0);

  useEffect(() => {
    const utilizationGap = targetUtilization - avgUtilization;
    const calculatedRevenue = (utilizationGap / 100) * bcbas * clientsPerBCBA * hoursPerMonth * hourlyRate * 12;
    setNetRevenuePotential(calculatedRevenue);
  }, [bcbas, avgUtilization, targetUtilization, clientsPerBCBA, hoursPerMonth, hourlyRate]);

  const handleNumberInput = (setter) => (event) => {
    let value = event.target.value.replace(/^0+/, '');
    if (value === '') {
      setter(0);
      event.target.value = '0';
    } else {
      const number = Number(value);
      if (!isNaN(number)) {
        setter(number);
        event.target.value = number.toString();
      }
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, backgroundColor: 'background.default' }}>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Avg. # of clients per BCBA
              </Typography>
              <TextField
                type="text"
                value={clientsPerBCBA}
                onChange={handleNumberInput(setClientsPerBCBA)}
                fullWidth
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Avg. Authorized hrs per client per month
              </Typography>
              <TextField
                type="text"
                value={hoursPerMonth}
                onChange={handleNumberInput(setHoursPerMonth)}
                fullWidth
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Avg. hourly rate for 97156
              </Typography>
              <TextField
                type="number"
                value={hourlyRate}
                onChange={handleNumberInput(setHourlyRate)}
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ min: 0 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                # of BCBAs
              </Typography>
              <Slider
                value={bcbas}
                onChange={(_, newValue) => setBcbas(newValue)}
                min={1}
                max={200}
                valueLabelDisplay="auto"
              />
              <Typography variant="body1" align="center">
                {bcbas}
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Current Utilization %
              </Typography>
              <Slider
                value={avgUtilization}
                onChange={(_, newValue) => setAvgUtilization(newValue)}
                min={0}
                max={100}
                valueLabelDisplay="auto"
              />
              <Typography variant="body1" align="center">
                {avgUtilization}%
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Target Utilization %
              </Typography>
              <Slider
                value={targetUtilization}
                onChange={(_, newValue) => setTargetUtilization(newValue)}
                min={0}
                max={100}
                valueLabelDisplay="auto"
              />
              <Typography variant="body1" align="center">
                {targetUtilization}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Card elevation={2} sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom align="center">
            Net Revenue Potential per Year
          </Typography>
          <Typography variant="h3" color="secondary" align="center">
            ${netRevenuePotential.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </Typography>
        </CardContent>
      </Card>
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button 
          variant="contained" 
          color="primary" 
          size="large"
          component={Link}
          href="https://www.joonhealth.co/company/demo-aba"
          target="_blank"
          rel="noopener noreferrer"
        >
          Schedule a free demo
        </Button>
      </Box>
    </Paper>
  );
};

export default BCBAUtilizationCalculator;